import React, { useState, useEffect, useCallback } from 'react';
import { FiDownload, FiSearch, FiFilter, FiBarChart2 } from 'react-icons/fi';
import { results as resultsApi, exams as examsApi } from '../../api';
import toast from 'react-hot-toast';

export default function ResultsPage() {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', examId: '', status: '', sortBy: 'percentage', sortOrder: 'desc', page: 1 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await resultsApi.getAll({ ...filters, limit: 50 });
      setData(res.data.data || []);
      setPagination(res.data.pagination || {});
    } catch { toast.error('Failed to load results'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    examsApi.getAll({ limit: 100 }).then(r => setExams(r.data.data || []));
  }, []);

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v, page: 1 }));

  const handleExport = async (fmt) => {
    try {
      const res = await resultsApi.export({ examId: filters.examId, format: fmt });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `results_${Date.now()}.${fmt === 'csv' ? 'csv' : 'xlsx'}`;
      a.click();
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-gray-900">Results</h1>
          <p className="text-gray-500 text-sm">{pagination.total || 0} total results</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => handleExport('csv')} className="btn-secondary text-sm py-2">
            <FiDownload size={14} /> CSV
          </button>
          <button onClick={() => handleExport('excel')} className="btn-primary text-sm py-2">
            <FiDownload size={14} /> Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input className="input pl-9" placeholder="Search candidate..." value={filters.search}
            onChange={e => setF('search', e.target.value)} />
        </div>
        <select className="input w-auto" value={filters.examId} onChange={e => setF('examId', e.target.value)}>
          <option value="">All Exams</option>
          {exams.map(e => <option key={e.id} value={e.id}>{e.title}</option>)}
        </select>
        <select className="input w-auto" value={filters.status} onChange={e => setF('status', e.target.value)}>
          <option value="">All</option>
          <option value="pass">Pass</option>
          <option value="fail">Fail</option>
        </select>
        <select className="input w-auto" value={`${filters.sortBy}_${filters.sortOrder}`}
          onChange={e => { const [by, order] = e.target.value.split('_'); setF('sortBy', by); setFilters(f => ({ ...f, sortOrder: order })); }}>
          <option value="percentage_desc">Highest Score</option>
          <option value="percentage_asc">Lowest Score</option>
          <option value="name_asc">Name A-Z</option>
          <option value="time_desc">Most Recent</option>
        </select>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="table-th">#</th>
                <th className="table-th">Candidate</th>
                <th className="table-th">Exam</th>
                <th className="table-th">Score</th>
                <th className="table-th">Percentage</th>
                <th className="table-th">Correct/Total</th>
                <th className="table-th">Time</th>
                <th className="table-th">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <tr key={i}>{[...Array(8)].map((_, j) => <td key={j} className="table-td"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>)}</tr>
                ))
              ) : data.length === 0 ? (
                <tr><td colSpan={8} className="py-16 text-center">
                  <FiBarChart2 size={32} className="text-gray-200 mx-auto mb-2" />
                  <p className="text-gray-400">No results found</p>
                </td></tr>
              ) : data.map((r, idx) => (
                <tr key={r.id} className="hover:bg-gray-50/50">
                  <td className="table-td text-gray-400 text-xs">{((filters.page - 1) * 50) + idx + 1}</td>
                  <td className="table-td">
                    <div>
                      <p className="font-semibold text-sm text-gray-900">{r.first_name} {r.last_name}</p>
                      <p className="text-xs text-gray-400">{r.email}</p>
                    </div>
                  </td>
                  <td className="table-td">
                    <p className="text-sm max-w-[150px] truncate font-medium">{r.exam_title}</p>
                  </td>
                  <td className="table-td font-bold text-gray-900">{parseFloat(r.raw_score).toFixed(1)} / {r.total_marks}</td>
                  <td className="table-td">
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-gray-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full" style={{ width: `${parseFloat(r.percentage)}%`, background: parseFloat(r.percentage) >= 60 ? '#10b981' : '#ef4444' }} />
                      </div>
                      <span className="text-sm font-bold">{parseFloat(r.percentage).toFixed(1)}%</span>
                    </div>
                  </td>
                  <td className="table-td text-sm text-gray-600">{r.correct_answers} / {r.total_questions}</td>
                  <td className="table-td text-xs text-gray-400">{Math.round(r.time_taken_seconds / 60)} min</td>
                  <td className="table-td">
                    <span className={r.is_passed ? 'badge-pass' : 'badge-fail'}>{r.is_passed ? 'PASS' : 'FAIL'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
            <p className="text-sm text-gray-500">Page {filters.page} of {pagination.totalPages}</p>
            <div className="flex gap-2">
              <button disabled={filters.page <= 1} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))} className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40">← Prev</button>
              <button disabled={filters.page >= pagination.totalPages} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))} className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40">Next →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
